import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap } from 'rxjs';
import * as fromGenerated from '../../../_generated';
import * as fromFeatureFlags from '../../../feature-flags';
import { sentryCaptureError } from '../../../sentry.helper';
import { convertLangToAssistantLocale } from '../assistants.helper';
import { AssistantActions, AssistantsActions } from './assistants.actions';

@Injectable()
export class AssistantsEffects {
  private readonly actions$ = inject(Actions);
  private readonly assistantsService = inject(fromGenerated.AssistantsService);
  private readonly translateService = inject(TranslateService);

  loadAssistants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantsActions.loadAssistants),
      switchMap(() =>
        this.assistantsService
          .assistantsControllerGetAssistantsV1(
            undefined,
            convertLangToAssistantLocale(
              this.translateService.getCurrentLang(),
            ),
          )
          .pipe(
            map((assistants) =>
              AssistantsActions.loadAssistantsSuccess({ assistants }),
            ),
            catchError((error: Error) =>
              of(
                AssistantsActions.loadAssistantsFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  onLoadFeatureFlags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromFeatureFlags.FeatureFlagsActions.loadFeatureFlags),
      map(() => AssistantsActions.loadAssistants()),
    ),
  );

  duplicateAssistant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.duplicateAssistant),
      switchMap(({ uuid }) =>
        this.assistantsService
          .assistantsControllerDuplicateAssistantV1(uuid, {
            locale: convertLangToAssistantLocale(
              this.translateService.getCurrentLang(),
            ),
          })
          .pipe(
            map((assistant) =>
              AssistantActions.duplicateAssistantSuccess({
                assistant: assistant,
              }),
            ),
            sentryCaptureError,
            catchError((error: Error) =>
              of(
                AssistantActions.duplicateAssistantFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteAssistant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.deleteAssistant),
      switchMap(({ uuid }) =>
        this.assistantsService.assistantsControllerDeleteAssistantV1(uuid).pipe(
          map(() => AssistantActions.deleteAssistantSuccess({ uuid })),
          sentryCaptureError,
          catchError((error: Error) =>
            of(
              AssistantActions.deleteAssistantFailure({ error: error.message }),
            ),
          ),
        ),
      ),
    ),
  );
}
